import { CSSProperties, useRef } from 'react';

import { ApiCorporateLocation, ApiPostLocation } from '~/api/v1/corporates/[id]/locations/get';
import { getOwner } from '~/domains/post/getOwner';
import { getOwnerLink } from '~/domains/post/getOwnerLink';
import { PostT } from '~/state/post';
import { OTHER_SOCIAL_ISSUE_CATEGORY } from '~/state/socialIssue';

import { useMBMap, UseMBMapOption } from './useMBMap';

const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;

export type MBMapProps = {
  style?: CSSProperties;
  /** @deprecated /activities のページ以外で使わない(/activitiesのページ用のapiのために残している) */
  posts?: PostT[];
  postLocations?: ApiPostLocation[];
  ownerLocations?: ApiCorporateLocation[];
  /** @default 'light' */
  theme?: UseMBMapOption['theme'];
  className?: string;
  minZoom?: number;
};

export function MBMap(props: MBMapProps) {
  const elRef = useRef<HTMLDivElement>(null);

  const theme = props.theme ?? 'light';
  useMBMap(
    { elRef, token: MAPBOX_ACCESS_TOKEN, theme, minZoom: props.minZoom ?? 3.1 },
    ({ mapboxgl, map }) => {
      type Option = {
        latitude: number;
        longitude: number;
        color: string;
        popupElement: HTMLElement;
      };
      const setMarkerForPost = (option: Option) => {
        const { latitude, longitude, color, popupElement } = option;

        const markerElement = createTag('div', {
          boxSizing: 'border-box',
          width: '20px',
          height: '20px',
          background: `linear-gradient(to bottom, white, 5%, ${color})`,
          borderRadius: '100%',
          borderColor: color,
        });
        const marker = new mapboxgl.Marker({ element: markerElement, color, scale: 0.8 });
        const popup = new mapboxgl.Popup().setDOMContent(popupElement);
        marker.setPopup(popup).setLngLat([longitude, latitude]);
        marker.addTo(map);
      };

      props.posts?.forEach((post) => {
        const { latitude, longitude } = post.location ?? {};
        if (!latitude || !longitude) return;
        const owner = getOwner(post);
        const { color } = post.social_issue?.social_issue_category ?? OTHER_SOCIAL_ISSUE_CATEGORY;

        const popupElement = createTag('a', {
          display: 'block',
          marginTop: '16px',
          color: '#007fee',
        });
        popupElement.href = getOwnerLink(owner, { postId: post.id });
        popupElement.innerHTML = `
        <span style="font-size: 11px; color: black;">${owner.name}</span>
        <br>
        <span style="text-decoration: underline;">${post.title}</span>
      `.trim();

        setMarkerForPost({ latitude, longitude, color, popupElement });
      });

      props.postLocations?.forEach((it) => {
        const { latitude, longitude, posts } = it;
        if (posts.length === 0 || !latitude || !longitude) return;
        const { color } = posts[0].social_issue.social_issue_category;

        const popupElement = createTag('div', {
          marginTop: '16px',
          color: 'black',
        });
        const postsLength = posts.length;
        popupElement.innerHTML = posts
          .map(({ title }) => {
            const maybeDot = postsLength === 1 ? '' : '・';
            return `${maybeDot}${title}`;
          })
          .join('<br>');

        setMarkerForPost({ latitude, longitude, color, popupElement });
      });

      props.ownerLocations?.forEach((it) => {
        const { latitude, longitude, color } = it;
        if (!latitude || !longitude) return;

        const popup = new mapboxgl.Popup().setHTML(`
        <div style="color: black;">
          ${it.category ?? ''}
          <br />
          ${it.prefecture ?? ''}${it.address1 ?? ''}${it.address2 ?? ''}
        </div>
      `);
        const marker = new mapboxgl.Marker({ color, scale: 0.8 });
        marker.setPopup(popup).setLngLat([longitude, latitude]);
        marker.addTo(map);
      });
    },
  );

  if (!MAPBOX_ACCESS_TOKEN) {
    return (
      <div>
        地図を表示することができません。
        <br />
        mapboxのアクセストークンが
        <br />
        入力されていません。
      </div>
    );
  }

  return <div className={props.className} style={props.style} ref={elRef} />;
}

function createTag<T extends keyof HTMLElementTagNameMap>(
  tag: T,
  style?: Partial<CSSStyleDeclaration>,
) {
  const element = document.createElement(tag);
  Object.entries(style ?? {}).forEach(([key, value]) => {
    element.style[key as any] = `${value}`;
  });
  return element;
}
